import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Observable, Subject, timer } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class IdleTimeoutService implements OnDestroy {
    private resetIdle$: Subject<void> = new Subject();
    private stopIdle$: Subject<void> = new Subject();
    private lastActivityTimestamp: number = Date.now();
    private idleTimeoutSubject: Subject<void> = new Subject();
    private warningTimeoutSubject: Subject<void> = new Subject();
    private closeWarningDialogSubject: Subject<void> = new Subject();
    private warningCountdown$: Subject<number> = new Subject();
    private broadcastChannel: BroadcastChannel;
    private warningTimer$: any;
    private uniqueTabId = Date.now();
    private _idleTime!: number;
    private _warningTime!: number;
    private hasIdleTimeoutTriggered = false;
    private idleChecker: any;

    public get warningTime(): number {
        return this._warningTime;
    }
    private set warningTime(value: number) {
        this._warningTime = value;
    }

    public get idleTime(): number {
        return this._idleTime;
    }
    private set idleTime(value: number) {
        this._idleTime = value;
    }

    public get warningCountdown(): Observable<number> {
        return this.warningCountdown$.asObservable();
    }  
    
    constructor(private ngZone: NgZone) {
        this.broadcastChannel = new BroadcastChannel('idle-timeout');
        this.setupBroadcastChannelListener();
    }

    // First initialize idle values
    public initialize(idleTime: number, warningTime: number) {
        this.idleTime = idleTime * 60 * 1000;
        this.warningTime = warningTime * 60 * 1000;
    }

    // Main method to start user idle monitoring
    public startTimer() {
        this.monitorUserActivity();
        this.startIdleTimer();
        this.trackTabVisibility();
    }

    public stopTimer() {
        this.stopIdle$.next();
    }

    // If the idle activity subscription is no longer running, use this method to restart
    // Typically used after the idle time has been reached and the warning timer has been started
    // User chooses to continue on the warning, so restart idle timer
    public continueSession() {
        this.broadcastCloseWarningDialog();
        this.resetIdleTimer();
    }

    // Watch for user idle timeout has been reached
    // When triggered the idleTime has been expired and warning timer has started
    public get idleTimeout$(): Observable<void> {
        return this.idleTimeoutSubject.asObservable();
    }

    // Watch for warning timeout to have been reached
    // Normally log out user when this has been triggered
    public get warningTimeout$(): Observable<void> {
        return this.warningTimeoutSubject.asObservable();
    }

    // Watch for the warning dialog to be closed from another tab
    public get closeWarningDialog$(): Observable<void> {
        return this.closeWarningDialogSubject.asObservable();
    }

    private resetIdleTimer() {
        this.resetTimers();
        this.startIdleTimer();
        this.monitorUserActivity();
        this.broadcastActivity();
    }

    private monitorUserActivity() {
        const activityEvents$: Observable<number> = merge(
            fromEvent(window, 'keypress'),
            fromEvent(window, 'touchstart'),
            fromEvent(window, 'touchmove'),
            fromEvent(window, 'resize'),
            fromEvent(window, 'focus'),
            fromEvent(document, 'scroll'),
            fromEvent(document, 'wheel'),
            fromEvent(document, 'mousemove'),
            fromEvent(document, 'mousedown'),
            fromEvent(document, 'mouseup')
        ).pipe(
            // Map the event to the current timestamp for comparison
            map(() => Date.now()),
    
            // Only emit if the timestamp has changed (new activity)
            distinctUntilChanged(),
    
            // Stop monitoring when `stopIdle$` is emitted
            takeUntil(this.stopIdle$)
        );
    
        activityEvents$.subscribe(() => {
            this.resetTimers();
            this.broadcastActivity();
        });
    }
    

    private startIdleTimer() {
        if (this.idleChecker) {
            clearInterval(this.idleChecker);
        }
    
        this.ngZone.runOutsideAngular(() => {
            const checkInterval = 1000;
    
            this.idleChecker = setInterval(() => {
                const currentTime = Date.now();
                const timeElapsed = currentTime - this.lastActivityTimestamp;
    
                if (timeElapsed >= this.idleTime + this.warningTime) {
                    clearInterval(this.idleChecker);
                    this.stopTimer();
                    this.setUserTimeout();
                } else if (timeElapsed >= this.idleTime) {
                    clearInterval(this.idleChecker);
                    this.startWarningTimer();                }
            }, checkInterval);
        });
    
        this.resetIdle$.pipe(takeUntil(this.stopIdle$)).subscribe(() => {
            clearInterval(this.idleChecker);
        });
    }

    private startWarningTimer() {
        if (this.idleChecker) {
            clearInterval(this.idleChecker);
        }
        this.stopIdle$.next();
    
        if (!this.hasIdleTimeoutTriggered) {
            this.idleTimeoutSubject.next(); // Emit idle timeout event
    
            const warningEndTime = Date.now() + this.warningTime; // Set the warning end timestamp
    
            this.ngZone.runOutsideAngular(() => {
                this.warningTimer$ = timer(0, 250).pipe(
                    map(() => Math.max(0, Math.ceil((warningEndTime - Date.now()) / 1000))),
                    distinctUntilChanged(),
                    takeUntil(this.resetIdle$)
                ).subscribe(remainingTime => {
                    this.ngZone.run(() => {
                        if (remainingTime > 0) {
                            this.warningCountdown$.next(remainingTime);
                        } else {
                            this.setUserTimeout();
                            this.warningCountdown$.next(0);
                        }
                    });
                });
            });
    
            this.hasIdleTimeoutTriggered = true; // Mark as triggered
        }
    }   

    private resetTimers() {
        this.lastActivityTimestamp = Date.now();
        this.hasIdleTimeoutTriggered = false;
        this.resetIdle$.next();
        this.startIdleTimer();
    }

    private broadcastActivity() {
        this.broadcastChannel.postMessage({
            event: 'user-activity',
            timestamp: Date.now(),
            tabId: this.uniqueTabId
        });
    }

    private broadcastTimeout() {
        this.broadcastChannel.postMessage({
            event: 'user-timeout',
            tabId: this.uniqueTabId
        });
    }

    private broadcastCloseWarningDialog() {
        this.broadcastChannel.postMessage({
            event: 'close-warning-dialog',
            tabId: this.uniqueTabId
        });
    }

    private setUserTimeout() {
        this.broadcastTimeout();
        this.warningTimeoutSubject.next();  // Emit warning timeout event
        this.hasIdleTimeoutTriggered = false;
    }

    private setupBroadcastChannelListener() {
        this.broadcastChannel.onmessage = (messageEvent) => {
            const { event, tabId } = messageEvent.data;
            if (event === 'user-activity' && tabId !== this.uniqueTabId && !this.hasIdleTimeoutTriggered) {
                this.resetTimers();
            } else if (event === 'user-timeout' && tabId !== this.uniqueTabId) {
                // If another tab times out, trigger local timeout actions
                this.setUserTimeout();
            } else if (event === 'close-warning-dialog' && tabId !== this.uniqueTabId) {
                this.resetTimers();
                this.closeWarningDialogSubject.next();
            }
        };
    }

    private trackTabVisibility() {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                const timeElapsed = Date.now() - this.lastActivityTimestamp;

                if (timeElapsed >= this.idleTime + this.warningTime && !this.hasIdleTimeoutTriggered) {
                    this.setUserTimeout();
                } else if (timeElapsed >= this.idleTime && !this.hasIdleTimeoutTriggered) {
                    this.startWarningTimer();
                } else {
                    if(!this.hasIdleTimeoutTriggered){
                        this.resetTimers();
                    }
                }
            }
        });
    }

    ngOnDestroy() {
        this.stopTimer();
        if (this.idleChecker) {
            clearInterval(this.idleChecker);
        }
        if (this.warningTimer$) {
            this.warningTimer$.unsubscribe();
        }
        if (this.broadcastChannel) {
            this.broadcastChannel.close(); // Clean up the BroadcastChannel
        }
    }
    
}
